import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";

// ─── Owner: paste your video link here (YouTube, Vimeo or direct .mp4) and it appears on both pages automatically ───
export const VIDEO_URL = "/retreat-message.mp4";

const toEmbedUrl = (url) => {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export const VideoPlayer = ({ onPlay }) => {
  const embed = VIDEO_URL ? toEmbedUrl(VIDEO_URL) : null;
  const isYouTube = Boolean(embed && embed.includes("youtube.com/embed/"));
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!isYouTube) return undefined;
    let player;
    const init = () => {
      player = new window.YT.Player(iframeRef.current, {
        events: {
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) onPlay?.();
          },
        },
      });
    };
    if (window.YT && window.YT.Player) {
      init();
    } else {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = init;
    }
    return () => player?.destroy?.();
  }, [isYouTube, onPlay]);

  return (
    <motion.div {...fade(0.15)} data-testid="vsl-video-frame" className="relative">
      <div
        className="pointer-events-none absolute -inset-6 rounded-[2.5rem] opacity-30 blur-3xl"
        style={{ background: "radial-gradient(closest-side, #E8B7C4, transparent)" }}
        aria-hidden="true"
      />
      <div className="relative aspect-video overflow-hidden rounded-3xl border border-[rgba(163,74,107,0.4)] bg-elevated shadow-[0_40px_100px_rgba(163,74,107,0.14)]">
        {embed ? (
          <iframe
            ref={iframeRef}
            src={isYouTube ? `${embed}?enablejsapi=1` : embed}
            title="A message from the founder of Meno Flames & Finesse"
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : VIDEO_URL ? (
          <video src={VIDEO_URL} controls onPlay={onPlay} className="h-full w-full object-cover" />
        ) : (
          <button
            type="button"
            data-testid="vsl-video-placeholder"
            onClick={onPlay}
            className="flex h-full w-full flex-col items-center justify-center gap-5 bg-[radial-gradient(ellipse_at_center,#FFFFFF_0%,#F4E8E4_75%)] px-8 text-center"
          >
            <span className="flex h-20 w-20 items-center justify-center rounded-full border border-[#A34A6B]/40 bg-elevated shadow-[0_0_50px_rgba(163,74,107,0.3)] transition-transform duration-300 hover:scale-105">
              <Play className="ml-1 h-8 w-8 text-[#A34A6B]" fill="currentColor" />
            </span>
            <span className="max-w-md font-serif text-xl italic text-cream/85">
              A short video message from the founder will play here.
            </span>
            <span className="rounded-full border border-dashed border-[#A34A6B]/40 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#A34A6B]">
              Placeholder · awaiting your video
            </span>
          </button>
        )}
      </div>
    </motion.div>
  );
};
